import type GraphicsLayer from '@arcgis/core/layers/GraphicsLayer';
import type { ResourceSet } from 'components/IAP/hooks/resourceSetHooks';
import type { ArcgisMapView } from '../LayersTab/utils/arcgisView';
import { NULL_VALUE } from 'constants/displayConstants';
import { applyHeleneResourcePopupToGraphic } from '../LayersTab/cfa-demo/heleneResourcePopup';
import { CFA_HELENE_STORES_LAYER_ID } from '../LayersTab/cfa-demo/historicalHurricanesHeleneData';
import {
  buildPointFeatureGraphic,
  focusFeatureOnMap,
} from '../LayersTab/utils/focusFeatureOnMap';
import {
  DHS_REGION4_ASSETS_LAYER_ID,
  dhsFacilityIdFromResourceNotes,
} from '../NotificationsTab/dhsRegion4ThreatNotification';
import { heleneStoreIdFromResourceNotes } from '../NotificationsTab/memphisThreatNotification';
import { USCG_SYMBOLOGY_OPTIONS } from '../LayersTab/utils/uscgSymbology';
import { formatResourceThreats } from './ResourceThreatsField';
import type { ResourceRow, User } from './ResourceTabCard';

export function displayOrNull(
  value: string | number | boolean | null | undefined
): string {
  if (value == null || value === '') return NULL_VALUE;
  return String(value);
}

export function formatShortId(row: ResourceRow): string {
  return row.resource_set_id?.slice(0, 5) || NULL_VALUE;
}

export function formatResourceName(row: ResourceRow): string {
  if (!row.name) return NULL_VALUE;
  return row.owning_organization ? `${row.name} (EXTERNAL)` : row.name;
}

export function formatIncidentName(row: ResourceRow): string {
  return row.incident_details?.[0]?.name || row.incident?.name || NULL_VALUE;
}

export function formatRequestStatus(row: ResourceRow): string {
  return row.inRequest ? 'Requested' : 'Not Requested';
}

export function formatCoord(value: number | undefined): string {
  if (value == null) return NULL_VALUE;
  return String(Math.round(value * 100) / 100);
}

export function formatUser(user: User | undefined): string {
  if (!user) return NULL_VALUE;
  return user.userName || user.emailAddress || NULL_VALUE;
}

export function formatAssignees(assignees: User[] | undefined): string {
  if (!Array.isArray(assignees) || assignees.length === 0) return NULL_VALUE;
  const names = assignees
    .filter(Boolean)
    .map((assignee) => assignee.userName || assignee.emailAddress)
    .filter(Boolean);
  return names.length ? names.join(', ') : NULL_VALUE;
}

export function formatAorName(
  aors: { id: string; name?: string }[],
  id: string | undefined
): string {
  if (!id) return NULL_VALUE;
  return aors.find((aor) => aor.id === id)?.name || NULL_VALUE;
}

export function formatThreatsCell(threats: string[] | undefined): string {
  return formatResourceThreats(threats);
}

export function formatPersonsOnBoard(
  value: ResourceRow['persons_on_board']
): string {
  if (value == null || value === '') return NULL_VALUE;
  if (typeof value === 'string' || typeof value === 'number') {
    return String(value);
  }
  try {
    return JSON.stringify(value);
  } catch {
    return NULL_VALUE;
  }
}

export function formatSymbology(value: string | undefined): string {
  if (!value) return NULL_VALUE;
  return (
    USCG_SYMBOLOGY_OPTIONS.find((option) => option.type === value)?.label ||
    value
  );
}

function resourceCoords(resource: ResourceSet | undefined): {
  latitude?: number;
  longitude?: number;
} {
  const coordinates =
    resource?.geometry &&
    typeof resource.geometry === 'object' &&
    Array.isArray(resource.geometry.coordinates)
      ? resource.geometry.coordinates
      : undefined;
  const longitude =
    coordinates?.[0] != null ? Number(coordinates[0]) : undefined;
  const latitude =
    coordinates?.[1] != null ? Number(coordinates[1]) : undefined;
  return {
    latitude:
      latitude != null && !Number.isNaN(latitude) ? latitude : undefined,
    longitude:
      longitude != null && !Number.isNaN(longitude) ? longitude : undefined,
  };
}

function findLiveResourceGraphic({
  mapView,
  resource,
}: {
  mapView: ArcgisMapView;
  resource: ResourceSet;
}): __esri.Graphic | undefined {
  const candidateIds = [
    resource.resource_set_id,
    resource.id,
    dhsFacilityIdFromResourceNotes(resource.notes),
    heleneStoreIdFromResourceNotes(resource.notes),
  ].filter((id): id is string => !!id);

  const layers = [DHS_REGION4_ASSETS_LAYER_ID, CFA_HELENE_STORES_LAYER_ID]
    .map((id) => mapView.map?.findLayerById(id) as GraphicsLayer | undefined)
    .filter((layer): layer is GraphicsLayer => !!layer);

  for (const layer of layers) {
    const hit = layer.graphics.toArray().find((graphic) => {
      const attrs = graphic.attributes ?? {};
      const attrId = String(attrs.id ?? '');
      return candidateIds.some((id) => attrId === id);
    });
    if (hit) return hit;
  }
  return undefined;
}

/**
 * Zoom to the resource and open the ArcGIS popup. Does not open the React
 * details dialog.
 */
export function openResourcePopupOnMap({
  row,
  mapView,
  resourceBySetId,
  aors,
}: {
  row: ResourceRow;
  mapView: ArcgisMapView | null;
  resourceBySetId: Map<string, ResourceSet>;
  aors: { id: string; name?: string }[];
}): void {
  const resource = row.resource_set_id
    ? resourceBySetId.get(row.resource_set_id)
    : undefined;
  const fromResource = resourceCoords(resource);
  const latitude = row.latitude ?? fromResource.latitude;
  const longitude = row.longitude ?? fromResource.longitude;

  if (
    latitude == null ||
    longitude == null ||
    Number.isNaN(latitude) ||
    Number.isNaN(longitude) ||
    !mapView
  ) {
    return;
  }

  const popupResource =
    resource ??
    ({
      id: row.id,
      resource_set_id: row.resource_set_id ?? row.id,
      name: row.name,
      description: row.description ?? '',
      capabilities: row.capabilities ?? null,
      kind: row.kind,
      type: row.type,
      quantity: row.quantity ?? 0,
      status: row.status ?? '',
      asset_status: row.assetStatus,
      owning_organization: row.owning_organization,
      address: row.address,
      opcon: row.opcon,
      tacon: row.tacon,
      hull_tail_number: row.hull_tail_number,
      unit: row.unit,
      symbology: row.symbology,
      notes: row.notes,
      point_of_contact: row.pointOfContact?.userGuid,
      current_status: row.status,
      geometry: { type: 'Point', coordinates: [longitude, latitude] },
    } as ResourceSet);

  const aorNames = {
    opcon: aors.find((aor) => aor.id === popupResource.opcon)?.name,
    tacon: aors.find((aor) => aor.id === popupResource.tacon)?.name,
  };

  const liveGraphic = findLiveResourceGraphic({
    mapView,
    resource: popupResource,
  });
  if (liveGraphic) {
    applyHeleneResourcePopupToGraphic({
      graphic: liveGraphic,
      resource: popupResource,
      aorNames,
    });
    void focusFeatureOnMap({ mapView, graphic: liveGraphic });
    return;
  }

  const fallback = buildPointFeatureGraphic({
    longitude,
    latitude,
    title: popupResource.name,
    attributes: {
      id: popupResource.resource_set_id,
      name: popupResource.name,
    },
  });
  applyHeleneResourcePopupToGraphic({
    graphic: fallback,
    resource: popupResource,
    aorNames,
  });
  void focusFeatureOnMap({ mapView, graphic: fallback });
}
